import React, { useCallback, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { WebView, WebViewMessageEvent } from 'react-native-webview';
import { useTheme, makeThemedStyles, type V10Palette } from '../../v10/theme';

// ---------------------------------------------------------------------------
// RichTextEditor — WebView-hosted HTML rich-text editor for NEMOS notes.
//
// Mirrors src/components/notes/RichTextEditor.tsx (web, tiptap) at the
// markup level: both read/write the SAME `notes.content` column as HTML, so
// a note edited here renders identically when reopened on web (and vice
// versa) as long as the markup produced stays within the tags web's tiptap
// setup itself emits/understands.
//
// Web's actual toolbar (read from src/components/notes/RichTextEditor.tsx,
// the `Toolbar` component) exposes exactly: a font-size (px) number input,
// Bold, Italic, Strikethrough, Bullet List, Ordered List, Align
// Left/Center/Right, Outdent/Indent, and Insert Table. There is no
// Underline, H1/H2, blockquote, link, etc. button on web, so none are added
// here — toolbar parity is deliberate, not an oversight.
//
// Implementation: no tiptap/ProseMirror dependency is available on mobile
// (React Native can't run it), so the WebView hosts a plain
// `contentEditable` document and drives formatting with
// `document.execCommand` (bold/italic/strikeThrough/insertUnorderedList/
// insertOrderedList/justifyLeft|Center|Right/outdent/indent — all standard,
// still supported by the WKWebView/Android System WebView engines this
// library wraps), plus two small custom commands to match tiptap features
// execCommand doesn't cover 1:1:
//   - font size: wraps the selection in `<span style="font-size:Npx">`,
//     the same inline-style shape tiptap's custom FontSize extension emits
//     (`style="font-size:Npx"`), so web parses it as the same mark.
//   - table insert: inserts a literal `<table><tbody>...` HTML block (3x3,
//     header row) matching the column/row/header shape tiptap's
//     `insertTable({ rows: 3, cols: 3, withHeaderRow: true })` produces.
//     Row/column add/delete/table-delete context-menu ops from web's editor
//     are NOT reproduced here (out of scope for a first pass); existing
//     tables from web still render (plain HTML table), and edits to text
//     inside table cells work via contentEditable.
// ---------------------------------------------------------------------------

// Base64-encode UTF-8 text for safe injection into the WebView's inline
// <script> (sidesteps quote/backtick/script-tag escaping entirely — the
// WebView decodes it with atob + a UTF-8-safe unescape/decodeURIComponent
// unwrap). No RN `Buffer` global is assumed (Hermes doesn't provide one
// without an extra polyfill dep), so this is a small self-contained
// UTF-8 -> base64 encoder using only string/charCode primitives.
const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function toBase64Utf8(input: string): string {
  // UTF-16 JS string -> UTF-8 byte array
  const utf8 = encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (_m, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  let output = '';
  let i = 0;
  for (; i + 2 < utf8.length; i += 3) {
    const b0 = utf8.charCodeAt(i);
    const b1 = utf8.charCodeAt(i + 1);
    const b2 = utf8.charCodeAt(i + 2);
    output +=
      BASE64_CHARS[b0 >> 2] +
      BASE64_CHARS[((b0 & 3) << 4) | (b1 >> 4)] +
      BASE64_CHARS[((b1 & 15) << 2) | (b2 >> 6)] +
      BASE64_CHARS[b2 & 63];
  }
  const remaining = utf8.length - i;
  if (remaining === 1) {
    const b0 = utf8.charCodeAt(i);
    output += BASE64_CHARS[b0 >> 2] + BASE64_CHARS[(b0 & 3) << 4] + '==';
  } else if (remaining === 2) {
    const b0 = utf8.charCodeAt(i);
    const b1 = utf8.charCodeAt(i + 1);
    output += BASE64_CHARS[b0 >> 2] + BASE64_CHARS[((b0 & 3) << 4) | (b1 >> 4)] + BASE64_CHARS[(b1 & 15) << 2] + '=';
  }
  return output;
}

const EDITOR_HTML_TEMPLATE = (initialContent: string, t: V10Palette, fieldFill: string) => `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
<style>
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  html, body { margin: 0; padding: 0; height: 100%; background: ${fieldFill}; }
  body {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }
  #toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 6px 8px;
    background: ${t.glassStrong};
    border-bottom: 1px solid ${t.navyLine};
    flex-shrink: 0;
  }
  #toolbar button {
    border: 1px solid ${t.navyLine};
    background: ${t.navyRaised};
    border-radius: 6px;
    min-width: 30px;
    height: 30px;
    font-size: 14px;
    font-weight: 700;
    color: ${t.ivory};
    padding: 0 8px;
  }
  #toolbar button.active {
    background: ${t.gold};
    border-color: ${t.gold};
    color: ${t.onGold};
  }
  #toolbar .sep {
    width: 1px;
    height: 20px;
    background: ${t.navyLine};
    margin: 0 2px;
  }
  #fontSize {
    width: 44px;
    height: 30px;
    border: 1px solid ${t.navyLine};
    background: ${t.navyRaised};
    color: ${t.ivory};
    border-radius: 6px;
    padding: 0 4px;
    font-size: 13px;
  }
  #editor {
    flex: 1;
    overflow-y: auto;
    padding: 12px 14px;
    font-size: 15px;
    line-height: 1.5;
    color: ${t.ivory};
    caret-color: ${t.gold};
    outline: none;
  }
  #editor ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
  #editor ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
  #editor ul li, #editor ol li { margin: 0.25em 0; }
  #editor table { width: 100%; border-collapse: collapse; border: 1px solid ${t.navyLine}; table-layout: fixed; margin: 1em 0; }
  #editor th, #editor td { border: 1px solid ${t.navyLine}; padding: 6px 10px; text-align: left; vertical-align: top; }
  #editor th { background: ${t.glass}; font-weight: 600; }
  #editor p { margin: 0 0 0.6em 0; }
  #editor:empty:before {
    content: attr(data-placeholder);
    color: ${t.ivoryFaint};
    pointer-events: none;
  }
</style>
</head>
<body>
  <div id="toolbar">
    <input id="fontSize" type="number" placeholder="px" />
    <div class="sep"></div>
    <button id="btn-bold" data-cmd="bold"><b>B</b></button>
    <button id="btn-italic" data-cmd="italic"><i>I</i></button>
    <button id="btn-strike" data-cmd="strikeThrough"><span style="text-decoration:line-through">S</span></button>
    <div class="sep"></div>
    <button id="btn-ul" data-cmd="insertUnorderedList">&#8226; List</button>
    <button id="btn-ol" data-cmd="insertOrderedList">1. List</button>
    <div class="sep"></div>
    <button id="btn-alignLeft" data-cmd="justifyLeft">&#8676;</button>
    <button id="btn-alignCenter" data-cmd="justifyCenter">&#8596;</button>
    <button id="btn-alignRight" data-cmd="justifyRight">&#8677;</button>
    <div class="sep"></div>
    <button id="btn-outdent" data-cmd="outdent">&#8676;&#124;</button>
    <button id="btn-indent" data-cmd="indent">&#124;&#8677;</button>
    <div class="sep"></div>
    <button id="btn-table">Table</button>
  </div>
  <div id="editor" contenteditable="true" data-placeholder="Start writing..."></div>

<script>
(function () {
  var editor = document.getElementById('editor');
  var RN = window.ReactNativeWebView;

  function post(type, payload) {
    if (RN && RN.postMessage) {
      RN.postMessage(JSON.stringify(Object.assign({ type: type }, payload || {})));
    }
  }

  // --- initial content injection (base64-encoded to sidestep any
  // script-context escaping issues with the raw HTML string).
  try {
    var decoded = decodeURIComponent(escape(window.atob("${toBase64Utf8(initialContent)}")));
    editor.innerHTML = decoded;
  } catch (e) {
    editor.innerHTML = '';
  }

  var saveTimer = null;
  function scheduleChange() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      post('change', { html: editor.innerHTML });
    }, 400);
  }

  editor.addEventListener('input', scheduleChange);
  editor.addEventListener('blur', function () {
    if (saveTimer) clearTimeout(saveTimer);
    post('change', { html: editor.innerHTML });
  });

  document.querySelectorAll('#toolbar button[data-cmd]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      editor.focus();
      document.execCommand(btn.getAttribute('data-cmd'), false, null);
      scheduleChange();
      updateToolbarState();
    });
  });

  var fontSizeInput = document.getElementById('fontSize');
  fontSizeInput.addEventListener('change', function () {
    var v = fontSizeInput.value;
    editor.focus();
    var sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    if (!v) return;
    var range = sel.getRangeAt(0);
    var span = document.createElement('span');
    span.style.fontSize = v + 'px';
    try {
      range.surroundContents(span);
    } catch (err) {
      var frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    scheduleChange();
  });

  document.getElementById('btn-table').addEventListener('click', function (e) {
    e.preventDefault();
    editor.focus();
    var rows = 3, cols = 3;
    var html = '<table><tbody>';
    for (var r = 0; r < rows; r++) {
      html += '<tr>';
      for (var c = 0; c < cols; c++) {
        html += r === 0 ? '<th>&nbsp;</th>' : '<td>&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    document.execCommand('insertHTML', false, html);
    scheduleChange();
  });

  function updateToolbarState() {
    var map = {
      'btn-bold': 'bold',
      'btn-italic': 'italic',
      'btn-strike': 'strikeThrough',
      'btn-ul': 'insertUnorderedList',
      'btn-ol': 'insertOrderedList',
      'btn-alignLeft': 'justifyLeft',
      'btn-alignCenter': 'justifyCenter',
      'btn-alignRight': 'justifyRight',
    };
    Object.keys(map).forEach(function (id) {
      var el = document.getElementById(id);
      var isActive = false;
      try { isActive = document.queryCommandState(map[id]); } catch (e) {}
      if (isActive) el.classList.add('active'); else el.classList.remove('active');
    });
  }

  document.addEventListener('selectionchange', function () {
    updateToolbarState();
  });

  post('ready', {});
})();
</script>
</body>
</html>`;

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onError?: () => void;
}

export function RichTextEditor({ value, onChange, onError }: RichTextEditorProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const webviewRef = useRef<WebView>(null);
  const [hasErrored, setHasErrored] = useState(false);
  // The HTML document (with initial content baked in) is only built once —
  // the WebView owns the DOM after that; RN doesn't push `value` updates
  // back in on every keystroke (same "uncontrolled after mount" pattern the
  // web tiptap editor uses via editor.commands.setContent only when
  // value !== editor.getHTML(), i.e. only on genuine external content
  // swaps). The caller is expected to `key` this component by note id so
  // switching notes remounts the WebView with the new note's content
  // instead of diffing HTML into a live DOM.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const source = useMemo(() => ({ html: EDITOR_HTML_TEMPLATE(value || '', t, c.inputFill) }), []);

  const handleMessage = useCallback(
    (event: WebViewMessageEvent) => {
      try {
        const msg = JSON.parse(event.nativeEvent.data);
        if (msg.type === 'change' && typeof msg.html === 'string') {
          onChange(msg.html);
        }
      } catch {
        // ignore malformed bridge messages
      }
    },
    [onChange]
  );

  const handleWebViewError = useCallback(() => {
    setHasErrored(true);
    onError?.();
  }, [onError]);

  if (hasErrored) {
    return null;
  }

  return (
    <View style={styles.container}>
      <WebView
        ref={webviewRef}
        originWhitelist={['*']}
        source={source}
        onMessage={handleMessage}
        onError={handleWebViewError}
        onHttpError={handleWebViewError}
        style={styles.webview}
        scrollEnabled={true}
        hideKeyboardAccessoryView={true}
        keyboardDisplayRequiresUserAction={false}
      />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    container: {
      flex: 1,
      minHeight: 260,
      borderRadius: 14,
      overflow: 'hidden',
      backgroundColor: c.inputFill,
      borderWidth: 1,
      borderColor: t.navyLine,
    },
    webview: {
      flex: 1,
      backgroundColor: c.inputFill,
    },
  }),
);

export default RichTextEditor;
